'use client';

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { SyncJobStatus, SyncJobPhase, EmailCategory } from '@/lib/cloudflare/types';

export interface SyncProgress {
  jobId: string;
  status: SyncJobStatus;
  phase: SyncJobPhase;
  total: number;
  processed: number;
  analyzed: number;
  cached: number;
  failed: number;
  percentComplete: number;
  estimatedTimeRemaining?: number;
  senderCount?: number;
}

export interface CategoryCount {
  do_nothing: number;
  delete: number;
  archive: number;
  unsubscribe: number;
}

export interface RecentEmail {
  id: string;
  subject: string;
  sender: string;
  category: EmailCategory;
  labels: string[];
}

interface SyncState {
  // Current sync job
  currentJobId: string | null;
  progress: SyncProgress | null;

  // Whether the active session has completed a sync state validation pass
  hasCheckedSyncState: boolean;

  // UI state
  isPolling: boolean;

  // Stats
  categoryCounts: CategoryCount;
  recentEmails: RecentEmail[];

  // Timestamps
  lastSyncCompletedAt: number | null;
  syncStartedAt: number | null;

  // Error handling
  error: string | null;

  // First-time user flag
  hasCompletedInitialSync: boolean;
}

interface SyncActions {
  // Job management
  startSync: (jobId: string, totalEmails: number) => void;
  updateProgress: (progress: SyncProgress) => void;
  completeSync: () => void;
  failSync: (error: string) => void;
  cancelSync: () => void;
  pauseSync: () => void;
  resumeSync: () => void;

  // UI actions
  setPolling: (polling: boolean) => void;
  setHasCheckedSyncState: (value: boolean) => void;

  // Stats
  updateCategoryCounts: (counts: CategoryCount) => void;
  addRecentEmail: (email: RecentEmail) => void;

  // Reset
  reset: () => void;
  resetForNewUser: () => void;
}

type SyncStore = SyncState & SyncActions;

const initialState: SyncState = {
  currentJobId: null,
  progress: null,
  isPolling: false,
  hasCheckedSyncState: false,
  categoryCounts: {
    do_nothing: 0,
    delete: 0,
    archive: 0,
    unsubscribe: 0,
  },
  recentEmails: [],
  lastSyncCompletedAt: null,
  syncStartedAt: null,
  error: null,
  hasCompletedInitialSync: false,
};

export const useSyncStore = create<SyncStore>()(
  persist(
    (set, get) => ({
      ...initialState,

      startSync: (jobId: string, totalEmails: number) => {
        set({
          currentJobId: jobId,
          progress: {
            jobId,
            status: 'processing',
            phase: 'queued',
            total: totalEmails,
            processed: 0,
            analyzed: 0,
            cached: 0,
            failed: 0,
            percentComplete: 0,
          },
          isPolling: true,
          syncStartedAt: Date.now(),
          error: null,
          categoryCounts: {
            do_nothing: 0,
            delete: 0,
            archive: 0,
            unsubscribe: 0,
          },
          recentEmails: [],
          hasCheckedSyncState: true,
        });
      },

      updateProgress: (progress: SyncProgress) => {
        set({
          progress,
          hasCheckedSyncState: true,
          error: null,
        });
      },

      completeSync: () => {
        const state = get();
        set({
          progress: state.progress ? {
            ...state.progress,
            status: 'completed',
            percentComplete: 100,
          } : null,
          isPolling: false,
          lastSyncCompletedAt: Date.now(),
          hasCompletedInitialSync: true,
          hasCheckedSyncState: true,
        });
      },

      failSync: (error: string) => {
        const state = get();
        set({
          progress: state.progress ? {
            ...state.progress,
            status: 'failed',
          } : null,
          isPolling: false,
          hasCheckedSyncState: true,
          error,
        });
      },

      cancelSync: () => {
        const state = get();
        set({
          progress: state.progress ? {
            ...state.progress,
            status: 'cancelled',
          } : null,
          isPolling: false,
          hasCheckedSyncState: true,
        });
      },

      pauseSync: () => {
        const state = get();
        set({
          progress: state.progress ? {
            ...state.progress,
            status: 'paused',
          } : null,
          isPolling: false, // Stop polling when paused
          hasCheckedSyncState: true,
        });
      },

      resumeSync: () => {
        const state = get();
        set({
          progress: state.progress ? {
            ...state.progress,
            status: 'processing',
          } : null,
          isPolling: true, // Resume polling
          hasCheckedSyncState: true,
        });
      },

      setPolling: (polling: boolean) => {
        set({ isPolling: polling });
      },

      setHasCheckedSyncState: (value: boolean) => {
        set({ hasCheckedSyncState: value });
      },

      updateCategoryCounts: (counts: CategoryCount) => {
        set({ categoryCounts: counts });
      },

      addRecentEmail: (email: RecentEmail) => {
        set((state) => ({
          recentEmails: [email, ...state.recentEmails].slice(0, 10),
          categoryCounts: {
            ...state.categoryCounts,
            [email.category]: state.categoryCounts[email.category] + 1,
          },
        }));
      },

      reset: () => {
        set({
          ...initialState,
          hasCheckedSyncState: false,
          hasCompletedInitialSync: get().hasCompletedInitialSync,
          lastSyncCompletedAt: get().lastSyncCompletedAt,
        });
      },

      resetForNewUser: () => {
        if (typeof window !== 'undefined') {
          localStorage.removeItem('sync-storage');
        }

        set({
          ...initialState,
        });
      },
    }),
    {
      name: 'sync-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        hasCompletedInitialSync: state.hasCompletedInitialSync,
        lastSyncCompletedAt: state.lastSyncCompletedAt,
        currentJobId: state.currentJobId,
        // Persist isPolling so Dynamic Island shows immediately on page refresh
        isPolling: state.isPolling,
        // Persist progress so we can restore sync state on refresh
        progress: state.progress,
        // Persist syncStartedAt so progress calculation survives page refresh
        syncStartedAt: state.syncStartedAt,
      }),
    }
  )
);
